import type {
  AssetServer,
  HostPayload,
  RmmState,
  TopologyNodeData,
  VmPayload,
} from '@interfaces';
import { useMemo } from 'react';
import type { Edge, Node } from 'reactflow';

export type RowItem = {
  key: string;
  entityId: string; // voor clicks
  type: 'host' | 'vm';
  name: string;
  hostName?: string;
  serial?: string;
  state?: string;
  rmm?: RmmState;
};

export type IssueItem = {
  entityId: string;
  name: string;
  type: 'Host' | 'VM';
  status: 'offline' | 'warning';
  reason: string;
  lastContact?: string;
};

export type SummaryStats = {
  totalHosts: number;
  totalVms: number;
  totalNodes: number;
  online: number;
  warning: number;
  offline: number;
  onlinePercent: number;
};

export type TopologyModel = {
  nodes: Node<TopologyNodeData>[];
  edges: Edge[];
  rows: RowItem[];
  issues: IssueItem[];
  stats: SummaryStats;
  nodeByEntityId: Record<string, Node<TopologyNodeData>>;
};

type Props = {
  servers: AssetServer[];
  rmmBySerial: Record<string, RmmState>;
  query: string;
  showOnlyRunning: boolean;
};

export function useServerTopologyModel({
  servers,
  rmmBySerial,
  query,
  showOnlyRunning,
}: Props): TopologyModel {
  const normalizedQuery = query.trim().toLowerCase();

  return useMemo(() => {
    const nodes: Node<TopologyNodeData>[] = [];
    const edges: Edge[] = [];
    const rows: RowItem[] = [];
    const issues: IssueItem[] = [];
    const nodeByEntityId: Record<string, Node<TopologyNodeData>> = {};

    // layout (zoals je originele code)
    let yOffset = 0;
    const MAX_VMS_PER_ROW = 6;
    const vmSpacing = 280;
    const leftPad = 40;
    const vmRowHeight = 180;

    // stats
    let totalHosts = 0;
    let totalVms = 0;
    let online = 0;
    let warning = 0;
    let offline = 0;

    const vmMatches = (vm: VmPayload) => {
      const qOk = !normalizedQuery
        ? true
        : (vm.name ?? '').toLowerCase().includes(normalizedQuery);
      const sOk = !showOnlyRunning ? true : /running/i.test(vm.state ?? '');
      return qOk && sOk;
    };

    const hostMatches = (host: HostPayload) => {
      if (!normalizedQuery) return true;
      const hostHit = (host.name ?? '').toLowerCase().includes(normalizedQuery);
      const vmHit = (host.vms ?? []).some((v) =>
        (v.name ?? '').toLowerCase().includes(normalizedQuery)
      );
      return hostHit || vmHit;
    };

    const pushRmm = (
      entityId: string,
      name: string,
      type: 'Host' | 'VM',
      rmm?: RmmState
    ) => {
      if (!rmm || rmm.loading || !rmm.present) return;

      if (rmm.status === 'online') online += 1;

      if (rmm.status === 'warning') {
        warning += 1;
        issues.push({
          entityId,
          name,
          type,
          status: 'warning',
          reason: rmm.reasons?.join(', ') ?? 'Warning',
          lastContact: rmm.lastContact,
        });
      }

      if (rmm.status === 'offline') {
        offline += 1;
        issues.push({
          entityId,
          name,
          type,
          status: 'offline',
          reason: rmm.reasons?.join(', ') ?? 'Offline',
          lastContact: rmm.lastContact,
        });
      }
    };

    servers
      .filter((s) => hostMatches(s.payload))
      .forEach((server) => {
        totalHosts += 1;

        const host = server.payload;
        const hostNodeId = server.SK;

        // entityId: liefst je backend id; fallback op SK
        const hostEntityId = String(host.id ?? server.SK);

        const hostRmm = host.serialNumber
          ? rmmBySerial[host.serialNumber]
          : undefined;

        // rows: host
        rows.push({
          key: `row-host-${hostEntityId}`,
          entityId: hostEntityId,
          type: 'host',
          name: host.name ?? server.SK,
          serial: host.serialNumber,
          state: host.state,
          rmm: hostRmm,
        });

        pushRmm(hostEntityId, host.name ?? server.SK, 'Host', hostRmm);

        // visible VMs
        const visibleVms = (host.vms ?? []).filter(vmMatches);
        totalVms += visibleVms.length;

        // host position = gecentreerd boven eerste VM row (zoals je code)
        const numVmRows = Math.ceil(visibleVms.length / MAX_VMS_PER_ROW) || 1;
        const vmsInFirstRow = Math.min(visibleVms.length, MAX_VMS_PER_ROW);
        const vmRowWidth = vmsInFirstRow * vmSpacing;
        const hostX = leftPad + Math.max(0, (vmRowWidth - 300) / 2);

        const hostNode: Node<TopologyNodeData> = {
          id: hostNodeId,
          type: 'host',
          position: { x: hostX, y: yOffset },
          data: {
            kind: 'host',
            host,
            updatedAt: server.updatedAt,
            rmm: hostRmm,
            serial: host.serialNumber,
            // handig voor debugging/click mapping
            // @ts-expect-error (als TopologyNodeData dit veld niet heeft, mag je dit weglaten)
            entityId: hostEntityId,
          } as unknown as TopologyNodeData,
        };

        nodes.push(hostNode);
        nodeByEntityId[hostEntityId] = hostNode;

        visibleVms.forEach((vm, index) => {
          const vmEntityId = String(
            vm.id ?? `${hostEntityId}:${vm.name ?? index}`
          );
          const vmNodeId = `${hostNodeId}-${vm.name}`;

          const vmRmm = vm.serialNumber
            ? rmmBySerial[vm.serialNumber]
            : undefined;

          // rows: vm
          rows.push({
            key: `row-vm-${vmEntityId}`,
            entityId: vmEntityId,
            type: 'vm',
            name: vm.name ?? '—',
            hostName: host.name,
            serial: vm.serialNumber,
            state: vm.state,
            rmm: vmRmm,
          });

          pushRmm(vmEntityId, vm.name ?? '—', 'VM', vmRmm);

          // layout
          const rowIndex = Math.floor(index / MAX_VMS_PER_ROW);
          const colIndex = index % MAX_VMS_PER_ROW;

          const vmX = leftPad + colIndex * vmSpacing;
          const vmY = yOffset + 185 + rowIndex * vmRowHeight;

          const vmNode: Node<TopologyNodeData> = {
            id: vmNodeId,
            type: 'vm',
            position: { x: vmX, y: vmY },
            data: {
              kind: 'vm',
              hostName: host.name,
              vm,
              rmm: vmRmm,
              // @ts-expect-error idem
              entityId: vmEntityId,
            } as unknown as TopologyNodeData,
          };

          nodes.push(vmNode);
          nodeByEntityId[vmEntityId] = vmNode;

          edges.push({
            id: `${hostNodeId}->${vmNodeId}`,
            source: hostNodeId,
            target: vmNodeId,
            type: 'vmLink',
            animated: true,
            data: {
              nic: vm.networks?.[0]?.switch ?? 'vSwitch',
              ip: Array.isArray(vm.networks?.[0]?.ipAddresses)
                ? vm.networks?.[0]?.ipAddresses[0]
                : vm.networks?.[0]?.ipAddresses,
              index,
            },
          });
        });

        yOffset += 185 + numVmRows * vmRowHeight + 40;
      });

    // issues sort: offline eerst, dan oudste lastContact
    issues.sort((a, b) => {
      if (a.status !== b.status) return a.status === 'offline' ? -1 : 1;
      if (!a.lastContact) return 1;
      if (!b.lastContact) return -1;
      return (
        new Date(a.lastContact).getTime() - new Date(b.lastContact).getTime()
      );
    });

    const totalNodes = totalHosts + totalVms;
    const onlinePercent =
      totalNodes > 0 ? Math.round((online / totalNodes) * 100) : 0;

    return {
      nodes,
      edges,
      rows,
      issues,
      stats: {
        totalHosts,
        totalVms,
        totalNodes,
        online,
        warning,
        offline,
        onlinePercent,
      },
      nodeByEntityId,
    };
  }, [servers, rmmBySerial, normalizedQuery, showOnlyRunning]);
}
